import { ComponentPropsWithRef } from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import {
  elevation00,
  primary01,
  primary02,
  primary03,
  semanticRed01,
  semanticRed02,
  shade0,
  shade100,
  shade200,
  shade700,
  shade850,
  shade950,
} from "../../styles/tokens";
import { normalStrongStyles } from "../body/Body.Parts";

// TEXT

// STYLES
export const Button = styled.button.attrs((props) => ({
  ...props,
  ...(props.as === Link && props.disabled && { to: undefined, as: undefined }),
}))<Props>`
  /* BASE */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  text-align: center;
  text-decoration: none;
  width: ${({ $fullWidth }) => $fullWidth && "100%"};
  gap: ${({ $size }) => ($size === "small" ? "0.25rem" : "0.5rem")};
  border: ${({ $variant }) =>
    $variant === "basic" ? `1px solid ${shade0}` : "none"};

  transition:
    box-shadow 0.3s ease,
    border-color 0.3s ease,
    color 0.3s ease,
    background-color 0.3s ease;

  /* FONT */
  ${normalStrongStyles}
  line-height: 1rem;

  /* $SIZE SETTINGS */
  height: ${({ $size }) =>
    $size === "large" ? "3.5rem" : $size === "small" ? "2.5rem" : "3rem"};
  padding: ${({ $size }) => ($size === "small" ? "0 1rem" : "0 1.5rem")};

  /* $VARIANT SETTINGS */
  color: ${({ $variant }) => ($variant === "important" ? shade950 : shade0)};

  background: ${({ $variant }) => {
    switch ($variant) {
      case "subtle":
      case "link":
        return "transparent";
      case "basic":
        return shade950;
      case "destructive":
        return semanticRed01;
      case "important":
        return shade0;
      case "primary":
      default:
        return primary01;
    }
  }};

  &[disabled],
  &:disabled {
    cursor: not-allowed;

    ${({ disabled, $variant }) =>
      disabled !== "functional" &&
      css`
        color: ${shade700};
        background: ${$variant === "link" ? "transparent" : shade100};
      `}
  }

  &:hover:not(:disabled, :active) {
    cursor: pointer;
    transition:
      background-color 50ms linear,
      transform 50ms linear;
    box-shadow: ${elevation00};
    background: ${({ $variant }) => {
      switch ($variant) {
        case "important":
          return shade200;
        case "subtle":
        case "basic":
          return shade850;
        case "destructive":
          return semanticRed02;
        case "link":
          return "transparent";
        case "primary":
        default:
          return primary02;
      }
    }};
  }

  &:active:not(:disabled) {
    cursor: pointer;
    transition: background-color 0.3s ease;
    background-color: ${({ $variant }) =>
      $variant === "subtle" ? shade850 : undefined};
    transform: scale(0.985);
  }

  &:focus-visible:not(:disabled) {
    outline: 2px solid ${shade0};
    outline-offset: 2px;
  }

  /* LINK VARIANT */
  ${({ $variant }) =>
    $variant === "link" &&
    css`
      height: auto;
      padding: 0;
      box-shadow: none;

      &:hover:not(:disabled, :active),
      &:active:not(:disabled) {
        transform: none;
        color: ${primary03};
      }

      &:disabled,
      &[disabled] {
        color: ${shade700};
      }
    `}
`;

export const LoadingIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  & > svg {
    width: 2.5rem;
  }
`;

export const LoadingButton = styled(Button)<{ $overlay: boolean }>`
  position: relative;

  & > :not(${LoadingIconWrapper}) {
    display: ${({ $overlay }) => ($overlay ? "contents" : "none")};
    visibility: hidden;
  }

  & > ${LoadingIconWrapper} {
    ${({ $overlay }) =>
      $overlay &&
      css`
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      `};
  }
`;

// DATA

// TYPES
export interface Props
  extends Omit<ComponentPropsWithRef<"button">, "disabled"> {
  /** Choose a size for the button */
  $size?: "normal" | "small" | "large";
  /** Stretch to fill the width of its container */
  $fullWidth?: boolean;
  /** What kind of action will this button perform? */
  $variant?:
    | "primary"
    | "important"
    | "subtle"
    | "basic"
    | "destructive"
    | "link";
  /**
   * Disables the button, making it unclickable
   * Can be set to "functional" to disable the button without changing its appearance
   */
  disabled?: boolean | "functional";
  /**
   * Toggle the loading state of the button
   * Can be given a string to display a loading message next to the loading icon
   */
  loading?: boolean | string;
}

// CONSTANTS

// FUNCTIONS

// ASSETS
